/* eslint-disable max-len */
import {
    useTheme,
} from '@mui/material/styles'
import {
    useCallback, useEffect,
    useMemo, useState,
} from 'react'
import {
    GridColumnVisibilityModel,
} from '@mui/x-data-grid-pro'

import useWindowSize from 'app/hooks/useWindowSize'

import {
    hideHiddenColumnsForFilter,
} from '../utils/FilterModel.utils'
import {
    ExtendedGridColDef,
} from '../SkyNetDataGrid.types'

type Props = {
    isDetailsView: boolean
    tableWidthPercent: number | undefined
    initialTableWidthPercent: number | undefined
    columns: ExtendedGridColDef[],
}

const NEW_COLUMN_BREAKPOINT = 12 // percent margin for each new column to show

const useVisibleColumns = ({
    isDetailsView,
    tableWidthPercent,
    initialTableWidthPercent,
    columns,
}: Readonly<Props>): {
    visibilityModel: GridColumnVisibilityModel,
    onColumnVisibilityModelChange: (model: GridColumnVisibilityModel, wasSetByUser?: boolean) => void
} => {
    const {
        width,
    } = useWindowSize()
    const {
        breakpoints,
    } = useTheme()

    const initialVisibilityModel = useMemo(() => {
        return hideHiddenColumnsForFilter(columns)
    }, [columns])

    const [
        visibilityModel,
        setVisibilityModel,
    ] = useState<GridColumnVisibilityModel>(initialVisibilityModel)
    const [
        visibilitySetByUser,
        setVisibilitySetByUser,
    ] = useState(false)

    useEffect(() => {
        setVisibilitySetByUser(false)
    }, [isDetailsView])

    // Performance optimization: calculate threshold to avoid recalculating on every pixel change
    const tableWidthThreshold = useMemo(() => {
        if (!tableWidthPercent) {
            return 0
        }
        return tableWidthPercent / 5
    }, [tableWidthPercent])

    const columnVisibilityModel = useMemo(() => {
        // In non-details view or without width data, show all non-hidden columns
        if (!isDetailsView || !tableWidthPercent || !initialTableWidthPercent) {
            return Object.fromEntries(
                columns.map((column) => {
                    return [
                        column.name,
                        !column.hiddenFromColumns,
                    ]
                }),
            )
        }
        // If user manually set visibility, respect their choice
        if (visibilitySetByUser) {
            return visibilityModel
        }

        let targetVisibleColumnsCount = 1

        const reducedColumns = columns.filter((column) => {
            return column.reduced
        })

        // On extra large screens, show all reduced columns instead of 1 default
        if (width >= breakpoints.values.xl && reducedColumns.length) {
            targetVisibleColumnsCount = Math.max(targetVisibleColumnsCount, reducedColumns.length)
        }

        // If table has grown beyond initial width plus breakpoint, show more columns
        if (tableWidthPercent > initialTableWidthPercent + NEW_COLUMN_BREAKPOINT) {
            const diff = tableWidthPercent - initialTableWidthPercent
            const additionalColumnsCount = Math.floor(diff / NEW_COLUMN_BREAKPOINT)

            targetVisibleColumnsCount = Math.max(
                targetVisibleColumnsCount,
                Math.min(1 + additionalColumnsCount, columns.length),
            )
        }
        targetVisibleColumnsCount = Math.min(targetVisibleColumnsCount, columns.length)

        return Object.fromEntries(
            columns.map((column, index) => {
                return [
                    column.name,
                    index < targetVisibleColumnsCount && !column.hiddenFromColumns,
                ]
            }),
        )

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        breakpoints.values.xl,
        initialTableWidthPercent,
        isDetailsView,
        tableWidthThreshold,
        width,
        columns,
        visibilitySetByUser,
    ])

    const onColumnVisibilityModelChange = useCallback((model: GridColumnVisibilityModel, wasSetByUser?: boolean) => {
        setVisibilityModel(model)
        if (wasSetByUser) {
            setVisibilitySetByUser(true)
        }
    }, [])

    // this optimization is crucial for tests to pass and reduce excessive renders
    const visibleColumnsCount = useMemo(() => {
        return Object.values(columnVisibilityModel).filter(Boolean).length
    }, [columnVisibilityModel])

    // This ensures that updates only occur when the actual values change.
    const memoizedColumnVisibilityModel = useMemo(() => {
        return columnVisibilityModel
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visibleColumnsCount])

    useEffect(() => {
        onColumnVisibilityModelChange(memoizedColumnVisibilityModel)
    }, [
        memoizedColumnVisibilityModel,
        onColumnVisibilityModelChange,
    ])

    return {
        visibilityModel,
        onColumnVisibilityModelChange,
    }
}

export default useVisibleColumns
